import { LoadingMojo } from "@classdojo/web";
import { BodyText, DisplayText } from "@classdojo/web/nessie";
import { RAW_cssValue, ThemeUIStyleObject } from "@classdojo/web/nessie/stylingLib";
import keyBy from "lodash/keyBy";
import { useEffect, useState } from "react";
import Author from "./Author";
import Follow from "./Follow";
import Note from "./Note";
import Discuss from "src/pods/ideas/components/cards/Discuss";
import DiscussWrapper from "src/pods/ideas/components/cards/DiscussWrapper";
import Watch from "src/pods/ideas/components/cards/Watch";
import getDiscussTopics, { DicussTopic } from "src/pods/ideas/data/getDiscussTopics";
import getWatch from "src/pods/ideas/data/getWatch";
import { Board as BoardType, Lesson, Slide } from "src/pods/ideas/data/types";

const MINDFUL_MOVEMENTS_LESSON_ID = "591a31b0e3fc2622b28b6ad6";

type BoardProps = {
  board: BoardType;
  displayDiscussCard?: (discuss: DicussTopic) => void;
  displayVideoCard?: (watch: Slide, lesson: Lesson, board: BoardType) => void;
};

export default function Board({ board, displayDiscussCard, displayVideoCard }: BoardProps) {
  const [lessons, setLessons] = useState<Lesson[]>([]);
  // bulk ignore existing useEffects
  // eslint-disable-next-line @web-monorepo/no-useEffect
  useEffect(() => {
    if (board) {
      import("../../../../../data/lessons.json").then(({ default: lessonsList }) => {
        const lessonsMap = keyBy(lessonsList, "url_slug");
        const lessons = board.ideas.map(({ url_slug: slug }) => lessonsMap[slug]);
        setLessons(lessons);
      });
    }
  }, [board, setLessons]);
  if (!board || !lessons.filter(Boolean).length) {
    return <LoadingMojo />;
  }

  return (
    <div sx={boardStyle}>
      <div>
        <div sx={boardHeaderStyle}>
          <div sx={ideasHerosStyle(board)} />
          <DisplayText as="h1" level={3} sx={boardHeaderTitleStyle}>
            {board.title}
          </DisplayText>
          <BodyText level={2} kind="tertiary" sx={boardHeaderDescriptionStyle}>
            {board.description}
          </BodyText>
          <Note>
            <Author name={board.author.name} label={board.author.label} photo={board.author.photo} />
            <Follow user={null} following={[]} boardSlug={board.slug} />
          </Note>
        </div>
        <div>
          {lessons.map((lesson) => {
            const videos = getWatch(lesson);
            const discuss = getDiscussTopics(lesson);
            const unbundledComponents = [];

            if (lesson && lesson.release_date && new Date(lesson.release_date) > new Date()) {
              return null;
            }

            if (Array.isArray(videos)) {
              if (lesson._id === MINDFUL_MOVEMENTS_LESSON_ID) {
                videos.forEach((watch, index) => {
                  unbundledComponents.push(
                    <Watch
                      key={`${lesson._id}-watch-${index}`}
                      watch={watch}
                      timing={getTimingOverride(lesson._id)}
                      lesson={{
                        ...lesson,
                        title: getTitleOverride(lesson._id, index),
                      }}
                      onClick={() => displayVideoCard?.(watch, lesson, board)}
                    />,
                  );
                });
              } else {
                videos.forEach((watch) => {
                  unbundledComponents.push(
                    <Watch
                      key={`${lesson._id}-watch`}
                      watch={watch}
                      lesson={lesson}
                      onClick={() => displayVideoCard?.(watch, lesson, board)}
                    />,
                  );
                });
              }
            }

            unbundledComponents.push(
              <DiscussWrapper key={`${lesson._id}-discuss`}>
                {discuss.map((question) => (
                  <Discuss key={question.text} topic={question.text!} onClick={() => displayDiscussCard?.(question)} />
                ))}
              </DiscussWrapper>,
            );

            return unbundledComponents;
          })}
        </div>
      </div>
    </div>
  );
}

function getTitleOverride(lessonId?: string, forIndex?: number) {
  if (lessonId === MINDFUL_MOVEMENTS_LESSON_ID) {
    switch (forIndex) {
      case 0:
        return "Mindful movements: Elephant";
      case 1:
        return "Mindful movements: Triangle";
      case 2:
        return "Mindful movements: Tree";
      default:
        throw new Error("unexpected case in getTitleOverride");
    }
  }
}

function getTimingOverride(lessonId?: string) {
  if (lessonId === MINDFUL_MOVEMENTS_LESSON_ID) {
    return "1 minute video";
  }
}

const boardStyle: ThemeUIStyleObject = {
  padding: RAW_cssValue("20px"),
  backgroundColor: "dt_background_primary",
};

const boardHeaderStyle: ThemeUIStyleObject = {
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  marginBottom: RAW_cssValue("20px"),
};

const boardHeaderTitleStyle: ThemeUIStyleObject = {
  textAlign: "center",
  width: "100%",
  overflow: "hidden",
  whiteSpace: "unset",
  textOverflow: "unset",
  marginTop: "dt_l",
};

const boardHeaderDescriptionStyle: ThemeUIStyleObject = {
  marginY: "dt_m",
};

function ideasHerosStyle(board: BoardType): ThemeUIStyleObject {
  return {
    backgroundPosition: "50%",
    objectFit: "contain",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    width: "180px",
    height: "180px",
    backgroundImage: `url(${board.image.replace(".png", "@3x.png")})`,
  };
}
